// admin-ui/src/features/b/BPage.jsx

import toast, {
  Toaster,
} from 'react-hot-toast';

import React, {
  useMemo,
  useRef,
  useState,
  useEffect,
} from 'react';

import {
  useForm,
} from 'react-hook-form';

import {
  Link,
} from 'react-router-dom';

import ky from 'ky';

import {
  GoogleMap,
  Marker,
  useLoadScript,
  Autocomplete,
} from '@react-google-maps/api';

import {
  useAuthRole,
} from '../../auth/AuthProvider.jsx';

import './b.css';


/**
 * API Client.
 *
 * 401/403 werden bewusst NICHT automatisch wiederholt.
 * Ein abgelaufener oder unzulässiger Login wird dadurch
 * nicht mehrfach an den Server geschickt.
 */
const API = ky.create({
  prefixUrl: '',

  retry: {
    limit: 2,

    methods: [
      'get',
    ],

    statusCodes: [
      408,
      429,
      500,
      502,
      503,
      504,
    ],

    backoffLimit: 2,
  },
});


const defaultCenter = {
  lat: 44.114,
  lng: 15.227,
};


export default function BPage() {

  /**
   * Die Session kommt aus unserem
   * zentralen AuthProvider.
   *
   * RequireRole hat bereits geprüft,
   * dass dieser Bereich nur für
   * admin/editor erreichbar ist.
   */
  const {
    session,
    user,
    userRole,
  } = useAuthRole();


  const accessToken =
    session?.access_token || null;


  // Google Maps laden
  const {
    isLoaded,
    loadError,
  } =
    useLoadScript({
      googleMapsApiKey:
        import.meta.env
          .VITE_GOOGLE_MAPS_API_KEY ??
        '',

      libraries: [
        'places',
      ],
    });


  // UI- & Daten-States
  const [
    categories,
    setCategories,
  ] =
    useState([]);


  const [
    catState,
    setCatState,
  ] =
    useState('idle');


  const [
    coords,
    setCoords,
  ] =
    useState(null);


  const [
    isSaving,
    setIsSaving,
  ] =
    useState(false);


  const acRef =
    useRef(null);


  // Formular
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState,
  } =
    useForm({
      mode: 'onChange',

      defaultValues: {
        name: '',
        description: '',
        address: '',
        phone: '',
        website: '',
        category_id: '',
        active: true,
      },
    });


  const {
    errors,
    isValid,
  } =
    formState;


  /**
   * Liefert den Bearer Header für unsere APIs.
   */
  function getAuthHeaders() {

    if (!accessToken) {
      throw new Error(
        'Keine gültige Supabase-Session vorhanden.',
      );
    }

    return {
      Authorization:
        `Bearer ${accessToken}`,
    };
  }


  // =====================================================
  // Kategorien laden
  // =====================================================

  useEffect(() => {

    let cancelled =
      false;


    async function loadCategories() {

      if (!accessToken) {

        setCatState(
          'error',
        );

        toast.error(
          'Keine gültige Anmeldung vorhanden.',
        );

        return;
      }


      setCatState(
        'loading',
      );


      try {

        const res =
          await API
            .get(
              '/api/categories',
              {
                searchParams: {
                  lang: 'de',
                  _t:
                    Date.now(),
                },

                cache:
                  'no-store',

                headers: {
                  Authorization:
                    `Bearer ${accessToken}`,
                },
              },
            )
            .json();


        if (cancelled) {
          return;
        }


        const normalized =
          (res ?? [])
            .map(
              (c) => ({
                ...c,

                label:
                  (
                    c.name_de ||
                    c.name_en ||
                    c.name ||
                    `#${c.id}`
                  ).trim(),
              }),
            );


        setCategories(
          normalized,
        );

        setCatState(
          'ok',
        );

      } catch (e) {

        console.error(
          'categories fetch failed',
          e,
        );


        if (
          cancelled
        ) {
          return;
        }


        setCatState(
          'error',
        );


        if (
          e.response?.status ===
          401
        ) {

          toast.error(
            'Session ungültig oder abgelaufen. Bitte neu anmelden.',
          );

        } else if (
          e.response?.status ===
          403
        ) {

          toast.error(
            'Keine Berechtigung für Modul B.',
          );

        } else {

          toast.error(
            'Kategorien konnten nicht geladen werden.',
          );

        }
      }
    }


    loadCategories();


    return () => {
      cancelled =
        true;
    };

  }, [
    accessToken,
  ]);


  // =====================================================
  // Map-Interaktionen
  // =====================================================

  const onMapClick =
    (e) => {

      const lat =
        e.latLng.lat();

      const lng =
        e.latLng.lng();


      setCoords({
        lat,
        lng,
      });


      requestAnimationFrame(
        () => {

          document
            .querySelector(
              'input[name="name"]',
            )
            ?.focus();

        },
      );
    };


  const onPlaceChanged =
    () => {

      const place =
        acRef.current
          ?.getPlace?.();


      if (!place) {
        return;
      }


      const lat =
        place.geometry
          ?.location
          ?.lat?.();


      const lng =
        place.geometry
          ?.location
          ?.lng?.();


      if (
        lat &&
        lng
      ) {

        setCoords({
          lat,
          lng,
        });

      }


      if (
        place.formatted_address
      ) {

        setValue(
          'address',
          place.formatted_address,
          {
            shouldValidate:
              true,
          },
        );

      }


      if (
        place.name &&
        !place.types
          ?.includes(
            'country',
          )
      ) {

        setValue(
          'name',
          place.name,
          {
            shouldValidate:
              true,
          },
        );

      }
    };


  // =====================================================
  // Submit
  // =====================================================

  const onSubmit =
    async (values) => {

      if (!coords) {

        toast.error(
          'Bitte zuerst Koordinaten auf der Karte setzen.',
        );

        return;
      }


      if (!accessToken) {

        toast.error(
          'Keine gültige Anmeldung vorhanden.',
        );

        return;
      }


      setIsSaving(
        true,
      );


      try {

        const payload = {

          ...values,

          desc:
            values.description,

          name:
            (
              values.name ||
              ''
            ).trim(),

          address:
            (
              values.address ||
              ''
            ).trim(),

          phone:
            (
              values.phone ||
              ''
            ).trim(),

          website:
            (
              values.website ||
              ''
            ).trim(),

          category_id:
            values.category_id
              ? Number(
                  values.category_id,
                )
              : null,

          lat:
            coords.lat,

          lng:
            coords.lng,
        };


        await API
          .post(
            '/api/locations',
            {
              json:
                payload,

              headers:
                getAuthHeaders(),
            },
          )
          .json();


        toast.success(
          'Marker gespeichert',
        );


        reset();

        setCoords(
          null,
        );


        setTimeout(
          () =>
            window.scrollTo({
              top: 0,
              behavior:
                'smooth',
            }),
          0,
        );

      } catch (e) {

        console.error(
          'save failed',
          e,
        );


        if (
          e.response
        ) {

          try {

            const txt =
              await e.response
                .text();


            try {

              const obj =
                JSON.parse(
                  txt,
                );


              toast.error(
                obj?.error ||
                  'Speichern fehlgeschlagen',
              );

            } catch {

              toast.error(
                `Speichern fehlgeschlagen (HTTP ${e.response.status})`,
              );

            }

          } catch {

            toast.error(
              'Speichern fehlgeschlagen',
            );

          }

        } else {

          toast.error(
            e?.message ||
              'Speichern fehlgeschlagen',
          );

        }

      } finally {

        setIsSaving(
          false,
        );

      }
    };


  // =====================================================
  // Map-Optionen
  // =====================================================

  const mapOptions =
    useMemo(
      () => ({
        fullscreenControl:
          true,

        mapTypeControl:
          true,

        streetViewControl:
          false,

        clickableIcons:
          false,
      }),
      [],
    );


  return (
    <div className="b-grid">

      <Toaster
        position="top-center"
        toastOptions={{
          duration: 3000,
        }}
      />


      {/* Menü-Button */}
      <div className="b-topbar-right">

        <Link
          className="b-menu-btn"
          to="/"
        >
          ← Menü
        </Link>

      </div>


      {/* Linke Spalte */}
      <div className="b-map">

        {loadError && (
          <div className="b-error">
            Google Maps Fehler – bitte
            VITE_GOOGLE_MAPS_API_KEY &
            Domain-Restriktionen prüfen.
          </div>
        )}


        {!loadError &&
          !isLoaded && (
            <div className="b-skeleton">
              Karte lädt …
            </div>
          )}


        {isLoaded && (
          <>

            <div className="b-search">

              <Autocomplete
                onLoad={
                  (ref) =>
                    (
                      acRef.current =
                        ref
                    )
                }

                onPlaceChanged={
                  onPlaceChanged
                }
              >

                <input
                  className="b-search-input"
                  type="text"
                  placeholder="Ort suchen…"
                  autoComplete="off"
                />

              </Autocomplete>

            </div>


            <GoogleMap
              mapContainerStyle={{
                width:
                  '100%',
                height:
                  '100%',
              }}

              center={
                coords ||
                defaultCenter
              }

              zoom={
                coords
                  ? 13
                  : 9
              }

              onClick={
                onMapClick
              }

              options={
                mapOptions
              }
            >

              {coords && (
                <Marker
                  position={
                    coords
                  }
                />
              )}

            </GoogleMap>

          </>
        )}

      </div>


      {/* Rechte Spalte */}
      <div className="b-panel">

        <h2
          style={{
            marginTop: 0,
          }}
        >
          Neuer Marker
        </h2>


        <p
          style={{
            marginTop: 0,
            color: '#555',
          }}
        >
          Koordinaten:
          Karte klicken …

          {coords && (
            <span
              className="b-coords"
              style={{
                marginLeft: 8,
              }}
            >
              {coords.lat.toFixed(
                5,
              )}
              ,{' '}
              {coords.lng.toFixed(
                5,
              )}
            </span>
          )}

        </p>


        <form
          onSubmit={
            handleSubmit(
              onSubmit,
            )
          }

          className="b-form"
        >

          <label>
            Name

            <input
              {...register(
                'name',
                {
                  required:
                    'Bitte Name angeben',
                },
              )}

              name="name"

              autoFocus={
                !coords
              }
            />

            {errors.name && (
              <small className="b-err">
                {
                  errors.name
                    .message
                }
              </small>
            )}

          </label>


          <label>
            Beschreibung

            <textarea
              rows={4}

              {...register(
                'description',
              )}
            />

          </label>


          <label>
            Adresse

            <input
              {...register(
                'address',
              )}
            />

          </label>


          <div className="b-row2">

            <label>
              Telefon

              <input
                {...register(
                  'phone',
                )}
              />

            </label>


            <label>
              Website

              <input
                placeholder="https://"

                {...register(
                  'website',
                )}
              />

            </label>

          </div>


          <label>
            Kategorie

            <select
              {...register(
                'category_id',
                {
                  required:
                    'Bitte Kategorie wählen',
                },
              )}

              disabled={
                catState !==
                'ok'
              }
            >

              {catState ===
                'loading' && (
                <option value="">
                  Lade Kategorien …
                </option>
              )}


              {catState ===
                'error' && (
                <option value="">
                  Fehler beim Laden
                </option>
              )}


              {catState ===
                'ok' && (
                <>

                  <option value="">
                    – wählen –
                  </option>


                  {categories.map(
                    (c) => (
                      <option
                        key={
                          c.id
                        }

                        value={
                          c.id
                        }
                      >
                        {
                          c.label
                        }
                      </option>
                    ),
                  )}

                </>
              )}

            </select>


            {errors.category_id && (
              <small className="b-err">
                {
                  errors
                    .category_id
                    .message
                }
              </small>
            )}

          </label>


          <label className="b-checkbox">

            <input
              type="checkbox"

              {...register(
                'active',
              )}
            />

            <span>
              aktiv
            </span>

          </label>


          <button
            className="b-primary"

            type="submit"

            disabled={
              !isValid ||
              !coords ||
              isSaving
            }
          >
            {isSaving
              ? 'Speichert …'
              : 'Speichern'}
          </button>

        </form>

      </div>

    </div>
  );
}